import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTES } from '@traction/shared';

export function ExtensionInstallPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const steps = [
    {
      number: 1,
      title: 'Download the Extension',
      description: 'Get the Traction Bee Chrome extension ZIP file for manual installation.',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      ),
      showDownload: true,
    },
    {
      number: 2,
      title: 'Install in Chrome',
      description: 'If loading manually: Go to chrome://extensions, enable Developer Mode, then drag the extension folder.',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      ),
    },
    {
      number: 3,
      title: 'Connect Your Account',
      description: 'Click the Connect button below to link your Traction Bee account to the extension.',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
    },
    {
      number: 4,
      title: 'Open Gmail',
      description: "Go to Gmail - you'll see a Traction Bee sidebar with your leads ready to contact.",
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
  ];

  const features = [
    {
      title: 'See Leads in Gmail',
      description: 'View your daily leads directly in Gmail without switching tabs.',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
    },
    {
      title: 'One-Click Compose',
      description: 'Click a lead to instantly compose an email with your AI-drafted message.',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      ),
    },
    {
      title: 'Quick Actions',
      description: 'Approve, skip, or reject leads without leaving your inbox.',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-cacao-dark">
      {/* Header */}
      <header className="border-b border-cacao-dark-tertiary bg-cacao-dark-secondary">
        <div className="mx-auto max-w-4xl px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to={ROUTES.DASHBOARD} className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-cacao-gold/20">
                <span className="text-lg">🐝</span>
              </div>
              <span className="font-semibold text-[#F5F5F5]">Traction Bee</span>
            </Link>
            {user && (
              <Link
                to={ROUTES.DASHBOARD}
                className="text-sm text-cacao-muted hover:text-[#F5F5F5] transition-colors"
              >
                Back to Dashboard
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-b from-cacao-gold/10 to-cacao-dark py-12">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-cacao-gold/20">
            <span className="text-4xl">🐝</span>
          </div>
          <h1 className="mt-6 text-3xl font-bold text-[#F5F5F5]">
            Gmail Extension
          </h1>
          <p className="mt-3 text-lg text-cacao-muted">
            Send personalized emails to your leads without leaving Gmail
          </p>
        </div>
      </div>

      {/* Main content */}
      <div className="mx-auto max-w-4xl px-4 py-12">
        {/* Features */}
        <div className="grid gap-6 md:grid-cols-3">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="rounded-xl bg-cacao-dark-secondary p-6 border border-cacao-dark-tertiary hover:border-cacao-gold/30 transition-colors"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cacao-gold/20 text-cacao-gold">
                {feature.icon}
              </div>
              <h3 className="mt-4 font-semibold text-[#F5F5F5]">{feature.title}</h3>
              <p className="mt-2 text-sm text-cacao-muted">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Installation Steps */}
        <div className="mt-12">
          <h2 className="text-xl font-bold text-[#F5F5F5]">How to Install</h2>
          <div className="mt-6 space-y-6">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className="flex gap-4 rounded-xl bg-cacao-dark-secondary p-6 border border-cacao-dark-tertiary"
              >
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-cacao-gold text-cacao-dark font-bold">
                  {step.number}
                </div>
                <div>
                  <h3 className="font-semibold text-[#F5F5F5]">{step.title}</h3>
                  <p className="mt-1 text-cacao-muted">{step.description}</p>
                  {index === 0 && (
                    <a
                      href="/traction-bee-extension.zip"
                      download
                      className="mt-3 inline-flex items-center gap-2 rounded-lg bg-cacao-gold px-4 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold/90 transition-colors"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download Extension ZIP
                    </a>
                  )}
                  {index === 2 && user && (
                    <button
                      onClick={() => navigate(ROUTES.EXTENSION_AUTH)}
                      className="mt-3 inline-flex items-center gap-2 rounded-lg bg-cacao-gold px-4 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold/90 transition-colors"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      Connect Extension
                    </button>
                  )}
                  {index === 2 && !user && (
                    <Link
                      to={ROUTES.LOGIN}
                      className="mt-3 inline-flex items-center gap-2 rounded-lg bg-cacao-gold px-4 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold/90 transition-colors"
                    >
                      Sign in to Connect
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Manual Install Instructions */}
        <div className="mt-12 rounded-xl bg-cacao-dark-secondary border border-cacao-dark-tertiary p-6">
          <h3 className="font-semibold text-[#F5F5F5]">Manual Installation (Developer Mode)</h3>
          <p className="mt-2 text-sm text-cacao-muted">
            If the extension isn't on the Chrome Web Store yet, follow these steps:
          </p>
          <a
            href="/traction-bee-extension.zip"
            download
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-cacao-gold px-4 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold/90 transition-colors"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download Extension ZIP
          </a>
          <ol className="mt-4 space-y-2 text-sm text-cacao-muted">
            <li className="flex gap-2">
              <span className="font-medium text-cacao-gold">1.</span>
              Click the download button above to get the ZIP file
            </li>
            <li className="flex gap-2">
              <span className="font-medium text-cacao-gold">2.</span>
              Unzip the file to a folder on your computer
            </li>
            <li className="flex gap-2">
              <span className="font-medium text-cacao-gold">3.</span>
              Open Chrome and go to <code className="rounded bg-cacao-dark-tertiary px-1.5 py-0.5 text-[#F5F5F5]">chrome://extensions</code>
            </li>
            <li className="flex gap-2">
              <span className="font-medium text-cacao-gold">4.</span>
              Enable "Developer mode" in the top right
            </li>
            <li className="flex gap-2">
              <span className="font-medium text-cacao-gold">5.</span>
              Click "Load unpacked" and select the extension folder
            </li>
          </ol>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-cacao-muted">
            Need help? Check our{' '}
            <Link to={ROUTES.SUPPORT} className="text-cacao-gold hover:underline">
              support page
            </Link>{' '}
            or{' '}
            <Link to={ROUTES.FAQ} className="text-cacao-gold hover:underline">
              FAQ
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
